import { apiFetch } from '@/lib/api-fetch'
import {
  type Marketplace,
  type StockGuardSettings,
} from '@/lib/api/schemas/stock-guard.schema'
import { z } from 'zod'

const LegacyPlanChannelSchema = z.object({
  population: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  unknown_status: z.number().int().nonnegative(),
  offer_unresolved: z.record(z.string(), z.number().int().nonnegative()),
  provider_unreadable: z.number().int().nonnegative(),
  blocked_mutations_disabled: z.number().int().nonnegative(),
  blocked_forced_dry_run: z.number().int().nonnegative(),
  blocked_switch_off: z.number().int().nonnegative(),
  write_zero: z.number().int().nonnegative(),
  write_one: z.number().int().nonnegative(),
  not_reached: z.number().int().nonnegative(),
  next_cursor: z.string().nullable(),
  last_run_at: z.string().datetime().nullable(),
  digest: z.string().nullable(),
}).strict()

async function parseJson(response: Response): Promise<unknown> {
  const body: unknown = await response.json()
  if (!response.ok) {
    const detail = typeof body === 'object' && body !== null && 'detail' in body
      ? String(body.detail)
      : `HTTP ${response.status}`
    throw new Error(detail)
  }
  return body
}

const LegacyStockGuardSettingsSchema = z.object({
  environment: z.enum(['local', 'preview', 'production']),
  dry_run: z.boolean(),
  settings: z.object({
    zero_on_wildberries: z.boolean(),
    zero_on_yandex: z.boolean(),
    restore_on_wildberries: z.boolean().default(false),
    restore_on_yandex: z.boolean().default(false),
  }).strict(),
  permitted: z.object({
    zero_on_wildberries: z.boolean(),
    zero_on_yandex: z.boolean(),
  }).strict(),
  plan_summary: z.object({
    ymarket: LegacyPlanChannelSchema,
    wildberries: LegacyPlanChannelSchema,
  }).strict().default({
    ymarket: {
      population: 0, unchanged: 0, unknown_status: 0, offer_unresolved: {},
      provider_unreadable: 0, blocked_mutations_disabled: 0,
      blocked_forced_dry_run: 0, blocked_switch_off: 0, write_zero: 0,
      write_one: 0, not_reached: 0, next_cursor: null, last_run_at: null, digest: null,
    },
    wildberries: {
      population: 0, unchanged: 0, unknown_status: 0, offer_unresolved: {},
      provider_unreadable: 0, blocked_mutations_disabled: 0,
      blocked_forced_dry_run: 0, blocked_switch_off: 0, write_zero: 0,
      write_one: 0, not_reached: 0, next_cursor: null, last_run_at: null, digest: null,
    },
  }),
}).strict()

function normalizeLegacySettings(value: unknown): StockGuardSettings {
  const parsed = LegacyStockGuardSettingsSchema.parse(value)
  return {
    environment: parsed.environment,
    dryRun: parsed.dry_run,
    settings: {
      zeroOnWildberries: parsed.settings.zero_on_wildberries,
      zeroOnYandex: parsed.settings.zero_on_yandex,
      restoreOnWildberries: parsed.settings.restore_on_wildberries,
      restoreOnYandex: parsed.settings.restore_on_yandex,
    },
    permitted: {
      zeroOnWildberries: parsed.permitted.zero_on_wildberries,
      zeroOnYandex: parsed.permitted.zero_on_yandex,
    },
    planSummary: {
      ymarket: {
        population: parsed.plan_summary.ymarket.population,
        unchanged: parsed.plan_summary.ymarket.unchanged,
        unknownStatus: parsed.plan_summary.ymarket.unknown_status,
        offerUnresolved: parsed.plan_summary.ymarket.offer_unresolved,
        providerUnreadable: parsed.plan_summary.ymarket.provider_unreadable,
        blockedMutationsDisabled: parsed.plan_summary.ymarket.blocked_mutations_disabled,
        blockedForcedDryRun: parsed.plan_summary.ymarket.blocked_forced_dry_run,
        blockedSwitchOff: parsed.plan_summary.ymarket.blocked_switch_off,
        writeZero: parsed.plan_summary.ymarket.write_zero,
        writeOne: parsed.plan_summary.ymarket.write_one,
        notReached: parsed.plan_summary.ymarket.not_reached,
        nextCursor: parsed.plan_summary.ymarket.next_cursor,
        lastRunAt: parsed.plan_summary.ymarket.last_run_at,
        digest: parsed.plan_summary.ymarket.digest,
      },
      wildberries: {
        population: parsed.plan_summary.wildberries.population,
        unchanged: parsed.plan_summary.wildberries.unchanged,
        unknownStatus: parsed.plan_summary.wildberries.unknown_status,
        offerUnresolved: parsed.plan_summary.wildberries.offer_unresolved,
        providerUnreadable: parsed.plan_summary.wildberries.provider_unreadable,
        blockedMutationsDisabled: parsed.plan_summary.wildberries.blocked_mutations_disabled,
        blockedForcedDryRun: parsed.plan_summary.wildberries.blocked_forced_dry_run,
        blockedSwitchOff: parsed.plan_summary.wildberries.blocked_switch_off,
        writeZero: parsed.plan_summary.wildberries.write_zero,
        writeOne: parsed.plan_summary.wildberries.write_one,
        notReached: parsed.plan_summary.wildberries.not_reached,
        nextCursor: parsed.plan_summary.wildberries.next_cursor,
        lastRunAt: parsed.plan_summary.wildberries.last_run_at,
        digest: parsed.plan_summary.wildberries.digest,
      },
    },
  }
}

export async function getStockGuardSettings(): Promise<StockGuardSettings> {
  return normalizeLegacySettings(await parseJson(await apiFetch('/api/settings/stock-guard')))
}

export async function updateStockGuardSettings(
  settings: StockGuardSettings['settings'],
): Promise<StockGuardSettings> {
  const response = await apiFetch('/api/settings/stock-guard', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      zero_on_wildberries: settings.zeroOnWildberries,
      zero_on_yandex: settings.zeroOnYandex,
      restore_on_wildberries: settings.restoreOnWildberries,
      restore_on_yandex: settings.restoreOnYandex,
    }),
  })
  return normalizeLegacySettings(await parseJson(response))
}

export function channelSettings(
  current: StockGuardSettings['settings'],
  marketplace: Marketplace,
  enabled: boolean,
): StockGuardSettings['settings'] {
  return marketplace === 'wildberries'
    ? { ...current, zeroOnWildberries: enabled }
    : { ...current, zeroOnYandex: enabled }
}
